'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import AppImage from '../ui/AppImage';
import Icon from '../ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const { user, loading, userProfile } = useAuth();

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const navigationItems = [
    { label: 'Home', href: '/homepage' },
    { label: 'Services', href: '/services' },
    { label: 'Why Danny Clans', href: '/why-danny-clans' },
    { label: 'Contact', href: '/contact' },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-card shadow-institutional">
      <div className="mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-16">
          <Link href="/homepage" className="flex items-center">
            <div className="flex items-center">
              <AppImage
                src="/assets/images/Danny_Clans_White-10-10-1769419143503.png"
                alt="Danny Clans Media Logo"
                width={270}
                height={90}
                className="object-contain"
              />
            </div>
          </Link>

          <nav className="hidden lg:flex items-center space-x-8">
            {/* Only show public navigation items if user is NOT authenticated */}
            {isHydrated && !loading && !user && navigationItems?.map((item) => (
              <Link
                key={item?.href}
                href={item?.href}
                className="font-body text-base text-text-primary hover:text-accent transition-colors duration-smooth"
              >
                {item?.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center space-x-4">
            <span className="font-accent text-sm text-text-secondary">
              HK Incorporated
            </span>
            {isHydrated && !loading && !user && (
              <Link
                href="/team/login"
                className="px-4 py-2 bg-secondary text-secondary-foreground font-headline font-semibold text-sm rounded-sm hover:bg-secondary/90 transition-colors duration-smooth"
              >
                Team Login
              </Link>
            )}
            <Link
              href="/contact"
              onClick={closeMenu}
              className="px-6 py-2.5 bg-cta text-cta-foreground font-headline font-semibold text-sm rounded-sm hover:bg-accent transition-colors duration-smooth shadow-institutional"
            >
              Professional Inquiry
            </Link>
          </div>

          <button
            onClick={toggleMenu}
            className="lg:hidden p-2 text-primary hover:text-accent transition-colors duration-smooth"
            aria-label="Toggle mobile menu"
          >
            <Icon
              name={isMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
              size={24}
              variant="outline"
            />
          </button>
        </div>
      </div>
      {isMenuOpen && (
        <div className="lg:hidden bg-card border-t border-border">
          <nav className="px-6 py-4 space-y-4">
            {/* Only show public navigation if user is NOT authenticated */}
            {isHydrated && !loading && !user && navigationItems?.map((item) => (
              <Link
                key={item?.href}
                href={item?.href}
                onClick={closeMenu}
                className="block font-body text-base text-text-primary hover:text-accent transition-colors duration-smooth py-2"
              >
                {item?.label}
              </Link>
            ))}
            <div className="pt-4 border-t border-border space-y-3">
              <div className="font-accent text-sm text-text-secondary">
                HK Incorporated
              </div>
              {isHydrated && !loading && user && (
                <>
                  {userProfile?.role === 'admin' && (
                    <>
                      <Link
                        href="/admin/dashboard"
                        onClick={closeMenu}
                        className="block w-full px-6 py-2.5 bg-purple-600 text-white font-headline font-semibold text-sm rounded-sm text-center hover:bg-purple-700 transition-colors duration-smooth"
                      >
                        User Management
                      </Link>
                      <Link
                        href="/lead-assignment"
                        onClick={closeMenu}
                        className="block w-full px-6 py-2.5 bg-blue-600 text-white font-headline font-semibold text-sm rounded-sm text-center hover:bg-blue-700 transition-colors duration-smooth"
                      >
                        Lead Assignment
                      </Link>
                    </>
                  )}
                  {userProfile?.role === 'sales_team' && (
                    <Link
                      href="/sales-dashboard"
                      onClick={closeMenu}
                      className="block w-full px-6 py-2.5 bg-green-600 text-white font-headline font-semibold text-sm rounded-sm text-center hover:bg-green-700 transition-colors duration-smooth"
                    >
                      Sales Dashboard
                    </Link>
                  )}
                  {(userProfile?.role === 'admin' || userProfile?.role === 'sub_admin' || userProfile?.role === 'sales_team') && (
                    <Link
                      href="/inquiry-management"
                      onClick={closeMenu}
                      className="block w-full px-6 py-2.5 bg-primary text-primary-foreground font-headline font-semibold text-sm rounded-sm text-center hover:bg-primary/90 transition-colors duration-smooth"
                    >
                      Inquiry Management
                    </Link>
                  )}
                  <Link
                    href="/dashboard"
                    onClick={closeMenu}
                    className="block w-full px-6 py-2.5 bg-accent text-accent-foreground font-headline font-semibold text-sm rounded-sm text-center hover:bg-accent/90 transition-colors duration-smooth"
                  >
                    Dashboard
                  </Link>
                </>
              )}
              {isHydrated && !loading && !user && (
                <Link
                  href="/team/login"
                  onClick={closeMenu}
                  className="block w-full px-6 py-2.5 bg-secondary text-secondary-foreground font-headline font-semibold text-sm rounded-sm text-center hover:bg-secondary/90 transition-colors duration-smooth"
                >
                  Team Login
                </Link>
              )}
              <Link
                href="/contact"
                onClick={closeMenu}
                className="block w-full px-6 py-2.5 bg-cta text-cta-foreground font-headline font-semibold text-sm rounded-sm text-center hover:bg-accent transition-colors duration-smooth shadow-institutional"
              >
                Professional Inquiry
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;